
import React, { useState, useEffect, useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import { gridContainerStyles } from '../NCO/style'
import MetricsToolbar from './MetricsToolbar'
import TotalsFooter from './TotalsFooter'
import { useEnrichedOrganizations } from '../../api/aws/useEnrichedOrganizations'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { computeFooterTotalsFromRows, computeMonthlyMetrics } from './computeMonthlyMetrics'
import { formatCurrency } from '../DealsKanban/aggregateStageTotals'
import { renderServiceIcon } from './renderServiceIcon'
import { useProductsTable } from '../../api/aws/products/useProductsTable'
import { useOrganizations } from '../../api/aws/useOrganizations'
import { Organization } from 'types'
import { EXCLUDED_KEYS } from './EXCLUDED_KEYS'

const MetricsDataGrid: React.FC = () => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const { showSnackbar } = useSnackbarContext()
  const { data: productsTable } = useProductsTable(true)
  const [grossTotals, setGrossTotals] = useState({})
  const { data: organizations } = useOrganizations(true, ['metrics', 'organizations'])
  const apiRef = useGridApiRef()

  const [selectedMonth, setSelectedMonth] = useState<string>('')
  const [selectedYear, setSelectedYear] = useState<string>('')
  const { enrichedOrganizations, isEnrichedOrganizationsLoading } = useEnrichedOrganizations(true)

  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [], quickFilterValues: [] })
  const [rowCount, setRowCount] = useState(0)
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const savedVisibility = localStorage.getItem('metricColumnVisibilityModel')
    return savedVisibility
      ? JSON.parse(savedVisibility)
      : {
          org_uuid: false,
          affiliated: false,
          source: false,
          street: false,
          city: false,
          state: false,
          zip: false,
          cmsUsed: false,
          social: false,
          dealIds: false,
        }
  })

  const processedOrganizations = useMemo(() => {
    if (!enrichedOrganizations) return []
    return enrichedOrganizations
      .filter((org: Organization) => Array.isArray(org.deals) && org.deals.length > 0)
      .map(org => {
        const newOrg = { ...org }
        if (!selectedMonth) {
          ;(org.aggregatedMetrics || []).forEach(metricObj => {
            const key = Object.keys(metricObj)[0]
            const normalizedKey = key === 'Paid Search - Media Spend' ? 'Paid Search Advertising' : key
            const value = metricObj[key] || 0
            newOrg[normalizedKey] = (newOrg[normalizedKey] || 0) + value
          })
        } else if (org.deals.length > 0) {
          const monthlyMetrics = computeMonthlyMetrics(org, selectedMonth)
          Object.keys(monthlyMetrics).forEach(service => {
            newOrg[service] = monthlyMetrics[service]
          })
        }
        return newOrg
      })
  }, [enrichedOrganizations, selectedMonth])

  const filteredOrganizations = useMemo(() => {
    return processedOrganizations.filter(org => {
      const matchesQuickFilter =
        !filterModel.quickFilterValues?.length ||
        filterModel.quickFilterValues.some(val => org.name?.toLowerCase().includes(val.toLowerCase()))

      const matchesFilters = filterModel.items.every(filter => {
        const value = org[filter.field]
        if (!value) return false
        if (filter.operator === 'contains') {
          return String(value).toLowerCase().includes(String(filter.value).toLowerCase())
        }
        if (filter.operator === 'equals') {
          return String(value) === String(filter.value)
        }
        return true
      })

      return matchesQuickFilter && matchesFilters
    })
  }, [processedOrganizations, filterModel])

  useEffect(() => {
    const totals = computeFooterTotalsFromRows(filteredOrganizations)
    setGrossTotals(totals)
    setRowCount(filteredOrganizations.length)
  }, [filteredOrganizations])

  const dynamicColumns = useMemo(() => {
    const union = new Set<string>()
    processedOrganizations.forEach(org => {
      ;(org.aggregatedMetrics || []).forEach(metricObj => {
        const key = Object.keys(metricObj)[0]
        if (key && typeof org[key] === 'number' && !EXCLUDED_KEYS.has(key)) {
          union.add(key)
        }
      })
    })
    const filteredKeys = Array.from(union).filter(key => (grossTotals[key] || 0) !== 0)
    return filteredKeys.map(key => ({
      field: key,
      headerName: key,
      renderHeader: () => (
        <Box>
          <img src={renderServiceIcon(key)} alt="icon" style={{ height: 28, width: 28 }} />
        </Box>
      ),
      width: 75,
      flex: 1,
      type: 'number',
      valueGetter: params => params || 0,
      renderCell: params => (params?.value > 0 ? formatCurrency(params?.value) : '–'),
    }))
  }, [processedOrganizations, grossTotals])

  const baseColumns: GridColDef[] = [
    { field: 'name', headerName: 'Organization Name', width: 150 },
    {
      field: 'deals',
      headerName: 'Deals Count',
      width: 150,
      valueGetter: (params: any) => params?.length || 0,
    },
    {
      field: 'contractedBy',
      headerName: 'Contracted By',
      flex: 1,
      valueGetter: params => organizations?.find(org => org?.org_uuid === params?.[0])?.name || '',
    },
  ]

  return (
    <Box sx={{ position: 'relative', ...gridContainerStyles, pt: pinned ? '60px' : 0, height: '98%' }}>
      <DataGridPro
        rows={filteredOrganizations}
        apiRef={apiRef}
        columns={[...baseColumns, ...dynamicColumns] as GridColDef[]}
        loading={isEnrichedOrganizationsLoading}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        slots={{ toolbar: MetricsToolbar as any, footer: TotalsFooter as any }}
        slotProps={{
          toolbar: {
            filterModel,
            visibilityModel: columnVisibilityModel,
            sortModel,
            setFilterModel,
            onClearSorting: () => setSortModel([{ field: 'name', sort: 'asc' }]),
            onClearVisibility: () => {
              const init = {
                org_uuid: false,
                affiliated: false,
                source: false,
                street: false,
                city: false,
                state: false,
                zip: false,
                cmsUsed: false,
                social: false,
                dealIds: false,
              }
              setColumnVisibilityModel(init)
              localStorage.setItem('metricColumnVisibilityModel', JSON.stringify(init))
            },
            selectedMonth,
            setSelectedMonth,
            selectedYear,
            setSelectedYear,
          } as any,
          footer: {
            grossTotals,
            dynamicColumns: [...baseColumns, ...dynamicColumns],
            rowCount,
          } as any,
        }}
        getRowId={row => row.org_uuid}
        sx={{ '& .MuiDataGrid-cell': { paddingRight: 1 }, '& .MuiDataGrid-columnHeaders': { borderBottom: 'none' } }}
        disableRowSelectionOnClick
      />
    </Box>
  )
}

export default MetricsDataGrid
// import React, { useState, useEffect, useMemo } from 'react'
// import { Box, useTheme } from '@mui/material'
// import {
//   DataGridPro,
//   GridColDef,
//   GridFilterModel,
//   GridSortModel,
//   GridValueGetter,
//   GridRenderCellParams,
//   useGridApiRef,
// } from '@mui/x-data-grid-pro'
// import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
// import { gridContainerStyles } from '../NCO/style'
// import MetricsToolbar from './MetricsToolbar'
// import TotalsFooter from './TotalsFooter'
// import { useEnrichedOrganizations } from '../../api/aws/useEnrichedOrganizations'
// import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
// import { computeFooterTotalsFromRows, computeMonthlyMetrics, computeOverallGrossTotals } from './computeMonthlyMetrics'
// import { formatCurrency } from '../DealsKanban/aggregateStageTotals'
// import { renderServiceIcon } from './renderServiceIcon'
// import { useProductsTable } from '../../api/aws/products/useProductsTable'
// import { useOrganizations } from '../../api/aws/useOrganizations'
// import { Organization } from 'types'
// import { EXCLUDED_KEYS } from './EXCLUDED_KEYS'

// const initialSortModel: GridSortModel = [
//   { field: 'creation_timestamp', sort: 'desc' },
//   { field: 'name', sort: 'asc' },
// ]

// const MetricsDataGrid: React.FC = () => {
//   const theme = useTheme()
//   const { pinned } = useLayoutContext()
//   const { showSnackbar } = useSnackbarContext()
//   const { data: productsTable, isLoading: isProductsTableLoading } = useProductsTable(true)
//   const [grossTotals, setGrossTotals] = useState({})
//   const { data: organizations, isLoading: isOrganizationsLoading } = useOrganizations(true, [
//     'metrics',
//     'organizations',
//   ])
//   const apiRef = useGridApiRef()

//   // Selected month in "YYYY-MM" format; empty string means no monthly filter.
//   const [selectedMonth, setSelectedMonth] = useState<string>('')
//   const [selectedYear, setSelectedYear] = useState<string>('')
//   // Fetch enriched organizations (overall data from the backend)
//   const { enrichedOrganizations, isEnrichedOrganizationsLoading } = useEnrichedOrganizations(true)

//   useEffect(() => {
//     if (!enrichedOrganizations) return
//     const totalPaidSearchMediaSpend = enrichedOrganizations
//       .flatMap(org => org.deals || [])
//       .flatMap(deal => deal.products || [])
//       .filter(product => {
//         const name = product?.productName
//         return name === 'Paid Search Advertising' || name === 'Paid Search - Media Spend'
//       })
//       .reduce((sum, product) => {
//         const spend = parseFloat(String(product.mediaSpend)) || 0
//         return sum + spend
//       }, 0)

//     console.log('🔍 Total Paid Search Media Spend:', totalPaidSearchMediaSpend.toFixed(2))
//   }, [enrichedOrganizations])

//   const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] })
//   const [rowCount, setRowCount] = useState(0)
//   const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }])
//   const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
//     const savedVisibility = localStorage.getItem('metricColumnVisibilityModel')
//     return savedVisibility
//       ? JSON.parse(savedVisibility)
//       : {
//           org_uuid: false,
//           affiliated: false,
//           source: false,
//           street: false,
//           city: false,
//           state: false,
//           zip: false,
//           cmsUsed: false,
//           social: false,
//           dealIds: false,
//         }
//   })

//   const processedOrganizations = useMemo(() => {
//     if (!enrichedOrganizations) return []

//     return enrichedOrganizations
//       .filter((org: Organization) => Array.isArray(org.deals) && org.deals.length > 0) // ✅ remove orgs with no deals
//       .map((org: Organization) => {
//         const newOrg = { ...org }

//         if (!selectedMonth) {
//           if (org.aggregatedMetrics && Array.isArray(org.aggregatedMetrics)) {
//             org.aggregatedMetrics.forEach(metricObj => {
//               const key = Object.keys(metricObj)[0]
//               const normalizedKey = key === 'Paid Search - Media Spend' ? 'Paid Search Advertising' : key
//               const value = metricObj[key] || 0
//               newOrg[normalizedKey] = (newOrg[normalizedKey] || 0) + value
//             })
//           }
//         } else {
//           if (org.deals && org.deals.length > 0) {
//             const monthlyMetrics = computeMonthlyMetrics(org, selectedMonth)
//             Object.keys(monthlyMetrics).forEach(service => {
//               newOrg[service] = monthlyMetrics[service]
//             })
//             newOrg['monthlyAggregatedMetrics'] = monthlyMetrics
//           } else if (org.monthlyAggregatedMetrics) {
//             Object.keys(org.monthlyAggregatedMetrics).forEach(service => {
//               const normalizedKey = service === 'Paid Search - Media Spend' ? 'Paid Search Advertising' : service
//               newOrg[normalizedKey] = org.monthlyAggregatedMetrics[service]
//             })
//           }
//         }

//         return newOrg
//       })
//   }, [enrichedOrganizations, selectedMonth])
//   const filteredOrganizations = useMemo(() => {
//     // If no filterModel, return all
//     if (!filterModel || !filterModel.items?.length) return processedOrganizations

//     return processedOrganizations.filter(org => {
//       return filterModel.items.every(filter => {
//         const value = org[filter.field]
//         if (!value) return false

//         // String contains logic
//         if (filter.operator === 'contains') {
//           return String(value).toLowerCase().includes(String(filter.value).toLowerCase())
//         }

//         // Equals logic
//         if (filter.operator === 'equals') {
//           return String(value) === String(filter.value)
//         }

//         return true
//       })
//     })
//   }, [processedOrganizations, filterModel])

//   // useEffect(() => {
//   //   const totals = computeFooterTotalsFromRows(processedOrganizations)
//   //   setGrossTotals(totals)
//   // }, [processedOrganizations])
//   useEffect(() => {
//     if (!apiRef.current) return

//     const updateVisibleTotals = () => {
//       const visibleIds = apiRef.current.getSortedRowIds()
//       const visibleRows = visibleIds.map(id => apiRef.current.getRow(id)).filter(Boolean)
//       const totals = computeFooterTotalsFromRows(visibleRows)
//       setGrossTotals(totals)
//     }

//     updateVisibleTotals()

//     const unsubFilter = apiRef.current.subscribeEvent('filterModelChange', updateVisibleTotals)
//     const unsubSort = apiRef.current.subscribeEvent('sortModelChange', updateVisibleTotals)
//     const unsubQuickFilter = apiRef.current.subscribeEvent('visibleRowsSet', updateVisibleTotals)

//     return () => {
//       unsubFilter?.()
//       unsubSort?.()
//       unsubQuickFilter?.()
//     }
//   }, [apiRef, processedOrganizations])

//   useEffect(() => {
//     const keys = new Set<string>()
//     processedOrganizations.forEach(org => {
//       Object.keys(org).forEach(k => {
//         if (typeof org[k] === 'number') keys.add(k)
//       })
//     })
//     // console.log('[METRICS] Fields used in totals:', [...keys].sort())
//   }, [processedOrganizations])

//   useEffect(() => {
//     if (processedOrganizations) {
//       setRowCount(processedOrganizations?.length || 0)
//     }
//   }, [processedOrganizations])

//   // Build dynamic columns by scanning each processed organization's keys.
//   const dynamicColumns = useMemo(() => {
//     const union = new Set<string>()
//     processedOrganizations.forEach(org => {
//       return (org.aggregatedMetrics || []).forEach(metricObj => {
//         const key = Object.keys(metricObj)[0]
//         if (key && typeof org[key] === 'number' && !EXCLUDED_KEYS.has(key)) {
//           union.add(key)
//         }
//       })
//     })

//     const filteredKeys = Array.from(union).filter(key => (grossTotals[key] || 0) !== 0)
//     return filteredKeys.map(key => ({
//       field: key,
//       headerName: key,
//       renderHeader: () => {
//         return (
//           <Box>
//             <img src={renderServiceIcon(key)} alt="seoColumnIcon" style={{ height: 28, width: 28 }} />
//           </Box>
//         )
//       },
//       width: 75,
//       flex: 1,
//       type: 'number',
//       valueGetter: params => params || 0,
//       // Optionally format the number as currency.
//       renderCell: params => (params?.value > 0 ? formatCurrency(params?.value) : '–'),
//     })) as GridColDef[]
//   }, [processedOrganizations])

//   // Define base columns.
//   const baseColumns: GridColDef[] = [
//     { field: 'name', headerName: 'Organization Name', width: 150 },
//     {
//       field: 'deals',
//       headerName: 'Deals Count',
//       width: 150,
//       valueGetter: (params: any) => params?.length || 0,
//     },
//     {
//       field: 'contractedBy',
//       headerName: 'Contracted By',
//       flex: 1,
//       valueGetter: params => {
//         const name = organizations?.find(org => org?.org_uuid === params?.[0])?.name
//         return name ?? ''
//       },
//     },
//   ]
//   const columns: GridColDef[] = [...baseColumns, ...dynamicColumns]

//   // Compute overall gross totals from the processed organizations,
//   // so that if a month is selected, it sums from the monthlyAggregatedMetrics.

//   // useEffect(() => {
//   //   if (!isProductsTableLoading && productsTable) {
//   //     console.log('productsTable', productsTable)
//   //   }
//   // }, [productsTable])
//   return (
//     <Box
//       sx={{
//         position: 'relative',
//         ...gridContainerStyles,
//         pt: pinned ? '60px' : 0,
//         transition: 'all 0.3s ease-in-out',
//         height: '98%',
//       }}
//     >
//       <DataGridPro
//         rows={filteredOrganizations}
//         apiRef={apiRef}
//         columns={columns}
//         loading={isEnrichedOrganizationsLoading}
//         filterModel={filterModel}
//         onFilterModelChange={newModel => setFilterModel(newModel)}
//         sortModel={sortModel}
//         onSortModelChange={newModel => setSortModel(newModel)}
//         slots={{
//           toolbar: MetricsToolbar,
//           footer: TotalsFooter,
//         }}
//         slotProps={{
//           toolbar: {
//             filterModel,
//             visibilityModel: columnVisibilityModel,
//             sortModel,
//             setFilterModel,
//             onClearSorting: (e: React.MouseEvent<HTMLButtonElement>) => {
//               e.preventDefault()
//               setSortModel([{ field: 'name', sort: 'asc' }])
//             },
//             onClearVisibility: (e: React.MouseEvent<HTMLButtonElement>) => {
//               e.preventDefault()
//               const init = {
//                 org_uuid: false,
//                 affiliated: false,
//                 source: false,
//                 street: false,
//                 city: false,
//                 state: false,
//                 zip: false,
//                 cmsUsed: false,
//                 social: false,
//                 dealIds: false,
//               }
//               setColumnVisibilityModel(init)
//               localStorage.setItem('metricColumnVisibilityModel', JSON.stringify(init))
//             },
//             selectedMonth: selectedMonth,
//             setSelectedMonth: setSelectedMonth,
//             selectedYear: selectedYear,
//             setSelectedYear: setSelectedYear,
//           },
//           footer: {
//             grossTotals,
//             dynamicColumns,
//             rowCount: rowCount ?? 0,
//           },
//         }}
//         getRowId={row => row.org_uuid}
//         sx={{
//           '& .MuiDataGrid-cell': { paddingRight: 1 },
//           '& .MuiDataGrid-columnHeaders': { borderBottom: 'none' },
//         }}
//         disableRowSelectionOnClick
//       />
//     </Box>
//   )
// }

// export default MetricsDataGrid
